<template>
  <div class="admin-survey-detail-wrap">
    <main v-if="item" class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/surveys" class="breadcrumb-link">Surveys</router-link>
        / {{ item.name }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ item.name }}</h1>
          <p v-if="item.description" class="page-subtitle">{{ item.description }}</p>
        </div>
        <div class="page-header-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onEditClick"
          >Edit</button>
          <button
            type="button"
            class="btn btn-danger-outline"
            @click="onDeleteClick"
          >Delete</button>
        </div>
      </div>

      <div class="meta-strip">
        <div class="meta-item">
          <span class="meta-label">Slug</span>
          <span class="meta-value mono">{{ item.slug }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Status</span>
          <span class="meta-value">
            <span class="status-pill" :class="`status-${item.status}`">{{ item.status }}</span>
          </span>
        </div>
        <div class="meta-item meta-item-questionnaire">
          <span class="meta-label">Questionnaire</span>
          <span class="meta-value">
            <router-link
              v-if="linkedQuestionnaire"
              :to="`/admin/questionnaires/${linkedQuestionnaire.slug}`"
              class="meta-link"
            >{{ linkedQuestionnaire.name }}</router-link>
            <span v-else class="dim">Loading…</span>
          </span>
          <span class="meta-subline">
            <span class="mono">#{{ item.questionnaire_id }}</span>
            <template v-if="linkedQuestionnaire">
              <span class="meta-subline-sep">·</span>
              <span class="mono">{{ linkedQuestionnaire.slug }}</span>
            </template>
          </span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Starts</span>
          <span class="meta-value">
            <template v-if="item.starts_at">{{ formatDateLong(item.starts_at) }}</template>
            <span v-else class="dim">Open immediately</span>
          </span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Ends</span>
          <span class="meta-value">
            <template v-if="item.ends_at">{{ formatDateLong(item.ends_at) }}</template>
            <span v-else class="dim">No end date</span>
          </span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Created</span>
          <span class="meta-value">{{ formatDateLong(item.created_at) }}</span>
        </div>
      </div>

      <section class="section">
        <h2 class="section-title">Collecting data</h2>

        <p v-if="item.status !== 'active'" class="section-intro">
          This survey is not open, so its link does nothing yet. Opening it freezes the
          questionnaire as a revision and binds this survey to it, so every participant's
          answers stay readable against the questions they actually saw.
        </p>
        <p v-else class="section-intro">
          This survey is open and collecting. Participants answer the revision it was
          bound to, not the live questionnaire, so editing a question cannot change the
          meaning of data already collected.
        </p>

        <div class="collect-row">
          <button
            v-if="item.status !== 'active'"
            type="button"
            class="btn btn-primary"
            :disabled="opening"
            @click="onOpenSurvey"
          >{{ opening ? 'Opening…' : 'Open survey for participants' }}</button>

          <template v-else>
            <span class="participant-label">Participant link</span>
            <code class="participant-link">{{ participantUrl }}</code>
            <button type="button" class="btn btn-secondary" @click="copyLink">
              {{ copyLabel }}
            </button>
          </template>
        </div>

        <p v-if="openNotice" class="open-notice">{{ openNotice }}</p>

        <div v-if="stats && stats.total > 0" class="export-row">
          <div class="export-text">
            <p class="export-title">Data export</p>
            <p class="export-hint">
              One Excel file with four sheets: the responses, a codebook explaining every
              column in all four languages, the revision history, and a README. Hand the
              whole file to a colleague; nothing else is needed to read it.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="exporting"
            @click="onDownloadExport"
          >{{ exporting ? 'Preparing…' : 'Download .xlsx' }}</button>
        </div>

        <div v-if="stats" class="stats-strip">
          <div class="stat"><span class="stat-value">{{ stats.total }}</span><span class="stat-label">Submissions</span></div>
          <div class="stat"><span class="stat-value">{{ stats.completed }}</span><span class="stat-label">Completed</span></div>
          <div class="stat"><span class="stat-value">{{ stats.in_progress }}</span><span class="stat-label">In progress</span></div>
          <div class="stat"><span class="stat-value">{{ stats.answers }}</span><span class="stat-label">Answers stored</span></div>
          <div class="stat"><span class="stat-value">{{ stats.revisions_spanned }}</span><span class="stat-label">Revisions spanned</span></div>
        </div>
      </section>

    </main>

    <main v-else-if="loading" class="page">
      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/surveys" class="breadcrumb-link">Surveys</router-link>
        / <span class="dim">{{ slug }}</span>
      </p>
      <p class="loading-state">Loading survey…</p>
    </main>

    <main v-else class="page">
      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/surveys" class="breadcrumb-link">Surveys</router-link>
        / {{ fetchError ? 'Error' : 'Not found' }}
      </p>
      <h1 class="page-title">
        {{ fetchError ? 'Could not load survey' : 'Survey not found' }}
      </h1>
      <p class="page-subtitle">
        <template v-if="fetchError">{{ fetchError }}</template>
        <template v-else>
          No survey matches the slug
          <code class="mono-inline">{{ slug }}</code>.
          It may have been deleted or never existed.
        </template>
      </p>
      <p>
        <router-link to="/admin/surveys" class="back-link">
          ← Back to all surveys
        </router-link>
      </p>
    </main>

    <SurveyFormModal
      v-if="showEdit && item"
      mode="edit"
      :initial="item"
      :server-error="editError"
      @submit="onEditSubmit"
      @cancel="onEditCancel"
    />

    <div
      v-if="showDelete && item"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-title"
      @click.self="onDeleteCancel"
    >
      <div class="modal modal-confirm">
        <h2 id="delete-title" class="modal-title">Delete {{ item.name }}?</h2>
        <p class="modal-text">
          This cannot be undone. To confirm, type the slug
          <code class="confirm-target">{{ item.slug }}</code>
          exactly.
        </p>

        <input
          ref="confirmInput"
          v-model="confirmText"
          type="text"
          class="confirm-input"
          :class="{ 'matched': confirmMatches }"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          aria-label="Type the slug to confirm deletion"
          :placeholder="item.slug"
          :disabled="deleting"
          @keyup.enter="confirmMatches && !deleting && confirmDelete()"
        >

        <p
          v-if="deleteError"
          class="modal-error"
          role="alert"
        >{{ deleteError }}</p>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="deleting"
            @click="onDeleteCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleting || !confirmMatches"
            @click="confirmDelete"
          >{{ deleting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import {
  findSurveyBySlug,
  removeSurvey,
  replaceSurvey,
  upsertSurvey,
  formatDateLong,
  dateInputToIso,
} from './_data/surveys.js';
import { questionnairesState } from './_data/questionnaires.js';
import SurveyFormModal from './SurveyFormModal.vue';

// We fetch the questionnaires list once per page mount so the linked
// questionnaire lookup (findById on the local store) succeeds even on
// direct URL loads or refreshes. Reuses the existing GET endpoint,
// no separate by-id endpoint needed. The store is shared so subsequent
// navigations to the questionnaires page or other survey details don't
// need to refetch.

export default {
  name: 'AdminSurveyDetail',
  components: { SurveyFormModal },
  data() {
    return {
      showEdit: false,
      editError: null,
      showDelete: false,
      deleting: false,
      deleteError: null,
      confirmText: '',
      loading: false,
      notFound: false,
      fetchError: null,
      opening: false,
      openNotice: null,
      exporting: false,
      stats: null,
      copyLabel: 'Copy',
      copyTimer: null,
    };
  },
  watch: {
    slug: {
      immediate: true,
      handler(newSlug) {
        this.fetchItem(newSlug);
        this.fetchStats(newSlug);
      },
    },
  },
  beforeUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    /**
     * The URL a participant is given. Built from the deployed origin
     * rather than hardcoded, so it is correct whether this is opened
     * against Cloud Run or a local server.
     */
    participantUrl() {
      return `${window.location.origin}/s/${this.slug}`;
    },
    item() {
      // Use the reactive store directly so re-renders happen on splice.
      return findSurveyBySlug(this.slug) || null;
    },
    linkedQuestionnaire() {
      if (!this.item) return null;
      return questionnairesState.items.find(q => q.id === this.item.questionnaire_id) || null;
    },
    confirmMatches() {
      return !!(this.item && this.confirmText === this.item.slug);
    },
  },
  methods: {
    async fetchStats(slug) {
      if (!slug) return;
      try {
        this.stats = await apiService.fetchSurveyStats(slug);
      } catch (e) {
        // Stats are informational. A survey page that fails to load
        // because a count could not be fetched would be worse than a
        // page without counts.
        this.stats = null;
      }
    },

    /**
     * Open the survey for participants.
     *
     * This is the single action that turns an authored questionnaire into
     * something that collects data: it freezes the questionnaire as a
     * revision if needed, binds this survey to it, and sets the survey
     * active. Deliberately one button rather than two, because "freeze,
     * then open, in that order" is a rule nobody should have to remember.
     */
    async onOpenSurvey() {
      this.opening = true;
      try {
        const result = await apiService.openSurvey(this.slug);
        await this.fetchItem(this.slug);
        await this.fetchStats(this.slug);
        const minted = result.revision_minted
          ? `Froze the questionnaire as revision ${result.revision} and opened the survey.`
          : `Opened on revision ${result.revision}.`;
        this.editError = null;
        this.openNotice = minted;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.fetchError = typeof detail === 'string'
          ? detail
          : 'Could not open this survey.';
      } finally {
        this.opening = false;
      }
    },

    /**
     * Survey name plus local date and time, so a folder of exports sorts
     * and reads sensibly:
     *     SEQ-First-Data-Store-Test_2026-08-09_0945.xlsx
     * The name rather than the slug, because the two can differ and the
     * name is what a colleague recognises.
     */
    exportFilename() {
      const pad = n => String(n).padStart(2, '0');
      const d = new Date();
      const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        + `_${pad(d.getHours())}${pad(d.getMinutes())}`;
      const name = (this.item?.name || this.slug)
        .trim()
        .replace(/[^\p{L}\p{N}]+/gu, '-')   // keep umlauts and accents
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
      return `${name}_${stamp}.xlsx`;
    },

    /**
     * Download the workbook. Fetched as a blob rather than opened as a
     * link so the request carries auth and a failure surfaces as a message
     * instead of a tab full of binary.
     */
    async onDownloadExport() {
      this.exporting = true;
      try {
        const { blob } = await apiService.downloadSurveyExport(this.slug);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Named here rather than server-side, because the browser is the
        // only party that knows the user's timezone. A file stamped in UTC
        // reads as two hours earlier than the moment it was downloaded,
        // which is confusing when sorting a folder of them.
        a.download = this.exportFilename();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoke on the next tick: Safari has been known to cancel the
        // download if the object URL disappears synchronously.
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (e) {
        this.fetchError = 'Could not build the export. Please try again.';
      } finally {
        this.exporting = false;
      }
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.participantUrl);
        this.copyLabel = 'Copied';
      } catch (e) {
        this.copyLabel = 'Copy failed';
      }
      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copyLabel = 'Copy'; }, 1500);
    },

    async fetchItem(slug) {
      // GET /api/seq/surveys/:slug. Refresh from the server on every
      // mount and on every route param change. Cached row stays
      // visible during the fetch; loading state shows only when there
      // is no cached row to render.
      if (!slug) return;
      this.loading = true;
      this.notFound = false;
      this.fetchError = null;
      try {
        const item = await apiService.fetchSurveyBySlug(slug);
        upsertSurvey(item);
        // Kick off the questionnaires fetch in parallel, used to
        // resolve the linked questionnaire's name and slug for the
        // meta strip. Non-fatal if it fails.
        this.fetchQuestionnairesForLink();
      } catch (e) {
        if (e?.response?.status === 404) {
          this.notFound = true;
        } else {
          const detail = e?.response?.data?.detail;
          this.fetchError = typeof detail === 'string'
            ? detail
            : 'Failed to load survey from the server.';
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchQuestionnairesForLink() {
      // Populate the shared questionnairesState so linkedQuestionnaire
      // can resolve from id → name + slug. We always refresh on mount
      // so a freshly-renamed questionnaire shows the new name on the
      // survey detail page, even if the user navigated directly here.
      try {
        const items = await apiService.fetchQuestionnaires();
        questionnairesState.items = items;
      } catch (e) {
        console.error('Failed to fetch questionnaires for link resolution:', e);
        // Keep going. If the call fails the meta cell falls back to
        // the bare "#<id>" rendering.
      }
    },
    onEditClick() {
      if (!this.item) return;
      this.editError = null;
      this.showEdit = true;
    },
    onEditCancel() {
      this.showEdit = false;
      this.editError = null;
    },
    async onEditSubmit(payload) {
      // PATCH /api/seq/surveys/:id. Backend ignores questionnaire_id
      // and slug (immutable), accepts name / description / starts_at /
      // ends_at / status. Dates come from the form as YYYY-MM-DD or
      // empty, so convert to ISO or null.
      this.editError = null;
      const id = this.item.id;
      const patch = {
        name: payload.name,
        description: payload.description,
        starts_at: dateInputToIso(payload.starts_at),
        ends_at: dateInputToIso(payload.ends_at),
        status: payload.status,
      };
      try {
        const updated = await apiService.updateSurvey(id, patch);
        replaceSurvey(updated);
        this.showEdit = false;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (Array.isArray(detail)) {
          this.editError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.editError = detail;
        } else {
          this.editError = 'Failed to update survey. Please try again.';
        }
      }
    },
    onDeleteClick() {
      this.deleteError = null;
      this.deleting = false;
      this.confirmText = '';
      this.showDelete = true;
      this.$nextTick(() => {
        const el = this.$refs.confirmInput;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },
    onDeleteCancel() {
      if (this.deleting) return;
      this.showDelete = false;
      this.deleteError = null;
      this.confirmText = '';
    },
    async confirmDelete() {
      // DELETE /api/seq/surveys/:id. Backend rejects deletion of
      // archived rows with a 409, the same archived-block rule we apply
      // to questionnaires. Surface the message in the modal so the
      // user can fix the status and retry.
      const id = this.item.id;
      this.deleteError = null;
      this.deleting = true;
      try {
        await apiService.deleteSurvey(id);
        removeSurvey(id);
        this.deleting = false;
        this.showDelete = false;
        this.$router.push('/admin/surveys');
      } catch (e) {
        this.deleting = false;
        const detail = e?.response?.data?.detail;
        if (Array.isArray(detail)) {
          this.deleteError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.deleteError = detail;
        } else {
          this.deleteError = 'Failed to delete survey. Please try again.';
        }
      }
    },
    formatDateLong,
  },
};
</script>

<style scoped>
.admin-survey-detail-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.admin-survey-detail-wrap *,
.admin-survey-detail-wrap *::before,
.admin-survey-detail-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 820px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.breadcrumb-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.breadcrumb-link:hover {
  border-bottom-color: var(--color-text-secondary);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 4px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-danger-outline {
  background: var(--color-bg-card);
  color: var(--color-bad);
  border-color: var(--color-bad-soft);
}

.btn-danger-outline:hover:not(:disabled) {
  background: var(--color-bad-soft);
  border-color: var(--color-bad);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 13px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.meta-value.mono {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 400;
  color: var(--color-text-secondary);
}

.meta-item-questionnaire {
  /* Give the questionnaire cell its own line in the strip. The flex
     container is wrap-enabled, so flex-basis: 100% pushes the
     remaining items (Starts / Ends / Created) to a second row. */
  flex: 1 1 100%;
  min-width: 0;
}

.meta-subline {
  font-size: 11px;
  color: var(--color-text-tertiary);
  font-weight: 400;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.meta-subline .mono {
  font-family: var(--font-mono);
  letter-spacing: 0.01em;
}

.meta-subline-sep {
  color: var(--color-text-tertiary);
  opacity: 0.6;
}

.meta-link {
  color: var(--color-accent);
  text-decoration: none;
  border-bottom: 1px solid rgba(83, 74, 183, 0.35);
}

.meta-link:hover {
  border-bottom-color: var(--color-accent);
}

.dim {
  color: var(--color-text-tertiary);
  font-weight: 400;
}

/* ── Collecting data section ── */
.section-intro {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.55;
  margin: 0 0 1rem;
  max-width: 68ch;
}

.collect-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.participant-label {
  font-size: 12px;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.participant-link {
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 6px 10px;
  border-radius: 6px;
  word-break: break-all;
}

.open-notice {
  font-size: 13px;
  color: var(--color-text-secondary);
  background: #F1F7F1;
  border: 0.5px solid #B9D4B9;
  border-radius: 8px;
  padding: 9px 12px;
  margin: 0 0 1rem;
}

.export-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 0.5px solid var(--color-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.export-text { flex: 1 1 320px; }

.export-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 4px;
}

.export-hint {
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin: 0;
  max-width: 62ch;
}

.stats-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.02);
  border: 0.5px solid var(--color-border);
  border-radius: 8px;
}

.stat { display: flex; flex-direction: column; gap: 2px; }
.stat-value { font-size: 18px; font-weight: 600; }

.stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
}

.status-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 9px;
  border-radius: 4px;
}

.status-pill.status-draft {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.status-pill.status-active {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.status-pill.status-closed {
  background: var(--color-bg-soft);
  color: var(--color-text-secondary);
  border: 0.5px solid var(--color-border);
}

.status-pill.status-archived {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

.placeholder-area {
  font-size: 14px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 28px 20px;
  text-align: center;
  margin: 0;
  font-style: italic;
}

.mono-inline {
  font-family: var(--font-mono);
  font-size: 13px;
  background: var(--color-bg-muted);
  padding: 2px 6px;
  border-radius: 4px;
}

.back-link {
  font-size: 14px;
  color: var(--color-accent);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

.loading-state {
  font-size: 14px;
  color: var(--color-text-tertiary);
  padding: 32px 0;
  text-align: center;
  margin: 0;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
  font-family: var(--font-sans);
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
  color: var(--color-text-primary);
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1rem;
  line-height: 1.5;
}

.modal-error {
  font-size: 13px;
  color: #6B1F18;
  background: rgba(200, 57, 46, 0.10);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.confirm-target {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 13px;
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  padding: 1px 8px;
  border-radius: 4px;
  user-select: all;
}

.confirm-input {
  display: block;
  width: 100%;
  margin: 0 0 1.25rem;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0.01em;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.confirm-input::placeholder {
  color: var(--color-text-tertiary);
  font-family: var(--font-mono);
}

.confirm-input:focus {
  outline: none;
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.confirm-input.matched {
  border-color: var(--color-bad);
  background: rgba(200, 57, 46, 0.06);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.18);
}

.confirm-input:disabled {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
  cursor: not-allowed;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.modal-confirm .modal-footer .btn {
  padding: 10px 16px;
}
</style>
